import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from '../hooks/useLocation';
import { Title } from '../components/ui/Title';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Ear, Star, MapPin, Users, User } from 'lucide-react';
import { ActionMenu } from '../components/ui/ActionMenu';
import { SEOHead } from '../components/SEOHead';
import { fetchMusicianDetails, type MusicianWithDetails } from '../services/api/musician';
import { formatDistance } from '../utils/distance';
import { useMusicTranslations } from '../utils/translations';

function MusicianDetails() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const { coordinates } = useLocation();
  const { translateGenre, translateSkill, translateMusicianStatus } = useMusicTranslations();
  const [musician, setMusician] = useState<MusicianWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadMusicianDetails() {
      if (!slug) return;

      try {
        const musicianData = await fetchMusicianDetails(slug, coordinates);
        setMusician(musicianData);
      } catch (err) {
        console.error('Error loading musician details:', err);
        setError(err instanceof Error ? err.message : 'Failed to load musician details');
      } finally {
        setLoading(false);
      }
    }

    loadMusicianDetails();
  }, [slug, coordinates]);

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-24">
        <div className="text-center text-light/60">{t('musician.loadingProfile')}</div>
      </div>
    );
  }

  if (error || !musician) {
    return (
      <div className="container mx-auto px-6 py-24">
        <div className="text-center text-red-500">{error || t('musician.notFound')}</div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: t('nav.musicians'), href: '/musicians' },
    { label: musician.artist_name || musician.full_name || 'Unnamed Musician' },
  ];

  // Generate structured data for the musician
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": musician.artist_name || musician.full_name,
    "alternateName": musician.full_name !== musician.artist_name ? musician.full_name : undefined,
    "description": musician.description,
    "image": musician.avatar_url,
    "url": `https://jamnation.ch/musicians/${musician.slug}`,
    "jobTitle": "Musician",
    "genre": musician.genres,
    "knowsAbout": musician.skills,
    "location": musician.location ? {
      "@type": "Place",
      "name": musician.location,
      ...(musician.coordinates && {
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": musician.coordinates.latitude,
          "longitude": musician.coordinates.longitude
        }
      })
    } : undefined,
    "memberOf": musician.bands.map(band => ({
      "@type": "MusicGroup",
      "name": band.name,
      "url": `https://jamnation.ch/bands/${band.slug}`,
      "genre": band.genres
    }))
  };

  return (
    <>
      <SEOHead
        title={`${musician.artist_name || musician.full_name} | Musiker:in${musician.location ? ` aus ${musician.location}` : ''}`}
        description={`Entdecke ${musician.artist_name || musician.full_name}${musician.location ? ` aus ${musician.location}` : ''}. ${musician.description || `Ein talentierter Musiker mit Fähigkeiten in ${musician.skills?.join(', ') || 'verschiedenen Bereichen'}.`} Vernetze dich auf jamnation.`}
        keywords={[
          musician.artist_name || musician.full_name || '',
          ...(musician.skills || []),
          ...(musician.genres || []),
          ...(musician.location ? [musician.location, `Musiker ${musician.location}`] : []),
          'Schweizer Musiker',
          'Musik Schweiz',
          'Musiker Schweiz'
        ].filter(Boolean)}
        url={`https://jamnation.ch/musicians/${musician.slug}`}
        image={musician.avatar_url}
        type="profile"
        structuredData={structuredData}
      />

      <div className="container mx-auto px-6 pb-8 md:py-24">
        <div className="mb-6">
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        <motion.div 
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, x: 200 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="panel mb-8">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-100 sm:w-40 aspect-square rounded-full bg-gray-custom/10 flex items-center justify-center overflow-hidden">
                {musician.avatar_url ? (
                  <img
                    src={musician.avatar_url}
                    alt={musician.artist_name}
                    className="w-full h-full object-cover"
                   loading="lazy" />
                ) : (
                  <User className="w-12 h-12 text-light/60" />
                )}
              </div>
              <div className="flex flex-col justify-center">
                <h1 className="text-3xl font-bold text-light mb-2">
                  {musician.artist_name}
                </h1>
                {musician.full_name && musician.artist_name !== musician.full_name && (
                  <p className="text-xl text-light/60 mb-2">{musician.full_name}</p>
                )}
                {musician.location && (
                  <div className="flex items-center text-light/40">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span>{musician.location}</span>
                    {musician.distance !== undefined && (
                      <span className="text-primary ml-2">
                        {formatDistance(musician.distance)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-12 mt-6">
              {musician.genres && musician.genres.length > 0 && (
                <div>
                  <h2 className="font-semibold text-xl mb-3">
                    {t('musician.genres')}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {musician.genres.map((genre) => (
                      <span
                        key={genre}
                        className="px-3 py-1 bg-tertiary rounded-full text-light"
                      >
                        {translateGenre(genre)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {musician.skills && musician.skills.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold mb-3">
                    {t('musician.skills')}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {musician.skills.map((skill) => (
                      <span
                        key={skill}
                        className="px-3 py-1 bg-secondary rounded-full text-light"
                      >
                        {translateSkill(skill)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* TODO: Re-set this with actual musician status */}
              <div>
                <h2 className="font-semibold text-xl mb-3">
                  Status
                </h2>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-primary rounded-full text-light">{translateMusicianStatus(musician.status)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Musician Description Section */}
          {musician.description && (
            <div className="mb-8">
              <div>
                <div className="border-l border-solid border-primary pl-6 text-light/60 whitespace-pre-line">{musician.description}</div>
              </div>
            </div>
          )}

          {musician.bands.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold mb-6">{t('musician.bands')}</h2>
              <div className="space-y-6">
                {musician.bands.map((band) => (
                  <Link
                    key={band.id}
                    to={`/bands/${band.slug}`}
                    className="block panel hover:bg-gray-custom/20"
                  >
                    <div className="flex items-center space-x-6">
                      <div className="w-32 h-32 bg-gray-custom/10 flex items-center justify-center overflow-hidden">
                        {band.avatar_url ? (
                          <img
                            src={band.avatar_url}
                            alt={band.name}
                            className="w-full h-full object-cover"
                           loading="lazy" />
                        ) : (
                          <Users className="w-8 h-8 text-light/60" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-light mb-2">{band.name}</h3>
                        <div className="text-sm text-light/60">
                          {band.location && (
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-1" />
                              <span>{band.location}</span>
                            </div>
                          )}
                        </div>
                        {band.genres && band.genres.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {band.genres.map((genre) => (
                              <span
                                key={genre}
                                className="px-2 py-0.5 text-xs bg-tertiary rounded-full text-light"
                              >
                                {genre}
                              </span>
                            ))}
                          </div>
                        )}
                        {band.instruments && band.instruments.length > 0 && (
                          <div className="flex flex-wrap items-center gap-2 mt-3">
                            {musician.artist_name && (
                              <span className="text-sm text-light/60">{musician.artist_name} {t('musician.plays')}</span>
                            )}
                            <div className="flex flex-wrap gap-2">
                              {band.instruments.map((instrument) => (
                                  <span
                                    key={instrument}
                                    className="px-2 py-0.5 bg-secondary rounded-full text-xs text-light"
                                  >
                                    {translateSkill(instrument)}
                                  </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </>
  );
}

export default MusicianDetails;